import { useSiteService } from 'src/services/site-service';
import { SiteFiltersDTOMatchMode } from 'src/api/models';
import type { SiteFiltersDTO } from 'src/api/models';
import type {
  EntitySearchConfig,
  EntitySearchResult,
  SiteEntity,
  PaginationParams,
  PaginatedResponse,
} from '../types';
import { ENTITY_ICONS, getEntityDisplayName, getEntityDisplayProps } from 'src/components/ticket-edit/entity-display';

function formatAddress(site: SiteEntity): string {
  const parts = [
    site.name,
    site.street,
    site.house_number,
    site.house_number_extension,
  ].filter(Boolean);

  return parts.join(' ');
}

function formatLocation(site: SiteEntity): string {
  const parts = [site.post_code, site.city, site.country].filter(Boolean);
  return parts.join(', ');
}

export function createSiteSearchConfig(): EntitySearchConfig<SiteEntity> {
  const { fetchSites, fetchSitesByOrganisation } = useSiteService();

  return {
    entityType: 'site',
    icon: ENTITY_ICONS.site,
    getDisplayName: (entity) => getEntityDisplayName(entity, 'site'),
    getDisplayProps: (entity) => getEntityDisplayProps(entity, 'site'),

    searchFields: [
      { name: 'street', weight: 2 },
      { name: 'city', weight: 2 },
      { name: 'post_code', weight: 1.5 },
      { name: 'description', weight: 1 },
    ],

    async fetchAll(pagination: PaginationParams): Promise<PaginatedResponse<SiteEntity>> {
      const response = await fetchSites({}, pagination);
      const page = response.page ?? 1;
      const totalPages = response.total_pages ?? 1;
      return {
        data: response.data || [],
        hasMore: page < totalPages,
        totalPages,
      };
    },

    async fetchByParent(
      organisationId: string,
      _pagination: PaginationParams
    ): Promise<PaginatedResponse<SiteEntity>> {
      const sites = await fetchSitesByOrganisation(organisationId);
      return {
        data: sites,
        hasMore: false,
      };
    },

    async apiSearch(
      query: string,
      pagination: PaginationParams,
      excludeIds?: string[]
    ): Promise<PaginatedResponse<SiteEntity>> {
      const filters: SiteFiltersDTO = {
        match_mode: SiteFiltersDTOMatchMode.or,
        street: query,
        city: query,
        description: query,
      };

      const response = await fetchSites(filters, pagination);
      let data = response.data || [];
      const page = response.page ?? 1;
      const totalPages = response.total_pages ?? 1;

      // Exclude specified IDs
      if (excludeIds && excludeIds.length > 0) {
        const excludeSet = new Set(excludeIds);
        data = data.filter((site) => site.id && !excludeSet.has(site.id));
      }

      return {
        data,
        hasMore: page < totalPages,
        totalPages,
      };
    },

    formatResult(site: SiteEntity, isRelated?: boolean): EntitySearchResult<SiteEntity> {
      const address = formatAddress(site);
      const location = formatLocation(site);

      return {
        id: site.id || '',
        title: address || 'No address',
        subtitle: location || site.description || '',
        icon: 'pi-map-marker',
        meta: {
          isRelated,
          // Sites don't have direct organisation reference in the model
          // The relationship is inferred from fetchByParent
        },
        originalItem: site,
      };
    },

    // Sites don't have a direct organisation_id in the model
    // They're fetched by organisation via the branches API
    getOrganisationId: undefined,
  };
}
